import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, RotateCcw, Rows3 } from 'lucide-react';
import { PrefSection, type PrefVariant } from '@/components/PrefSection';
import {
  SUMMARY_BLOCKS,
  SUMMARY_LAYOUT_DEFAULT,
  isDefaultSummaryLayout,
  useSummaryLayout,
} from '@/hooks/useSummaryLayout';
import { cn } from '@/lib/utils';

/**
 * Profile-page control to reorder the patient Summary's content blocks. Kept on
 * Profile (not the Summary itself) so the Summary stays uncluttered. Persists to
 * this browser; applies on the next Summary render.
 */
const LABELS = Object.fromEntries(SUMMARY_BLOCKS.map((b) => [b.key, b.label]));

export const SummaryLayoutPrefs = ({ variant = 'card' }: { variant?: PrefVariant }) => {
  const { t } = useTranslation();
  const [order, setOrder] = useSummaryLayout();

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
  };

  const atDefault = isDefaultSummaryLayout(order);

  return (
    <PrefSection
      variant={variant}
      icon={Rows3}
      title={t('Summary layout')}
      description={t('Reorder the sections on the patient Summary. Saved to this browser.')}
      action={
        <button
          type="button"
          onClick={() => setOrder(SUMMARY_LAYOUT_DEFAULT)}
          disabled={atDefault}
          className={cn(
            'flex items-center gap-1 text-xs transition-colors',
            atDefault
              ? 'cursor-not-allowed text-muted-foreground/40'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <RotateCcw className="h-3 w-3" />
          {t('Reset to default')}
        </button>
      }
    >
      <ul className="space-y-1.5">
        {order.map((key, i) => (
          <li
            key={key}
            className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{t(LABELS[key])}</span>
            <div className="flex shrink-0 items-center gap-1">
              <MoveButton
                dir="up"
                disabled={i === 0}
                onClick={() => move(i, -1)}
                label={t('Move up')}
              />
              <MoveButton
                dir="down"
                disabled={i === order.length - 1}
                onClick={() => move(i, 1)}
                label={t('Move down')}
              />
            </div>
          </li>
        ))}
      </ul>
    </PrefSection>
  );
};

const MoveButton = ({
  dir,
  disabled,
  onClick,
  label,
}: {
  dir: 'up' | 'down';
  disabled: boolean;
  onClick: () => void;
  label: string;
}) => {
  const Icon = dir === 'up' ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'flex h-7 w-7 items-center justify-center rounded-md border transition-colors',
        disabled
          ? 'cursor-not-allowed border-transparent text-muted-foreground/40'
          : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
};
